import { Checkbox, Text, useToast, type DataTableProps, type MenuItem } from '@ui';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage } from '../../../lib/errors';
import { useSession } from '../../identity/session';
import {
  useCreateInvoice,
  useSale,
  useSaleAction,
  type PaymentMethod,
  type SaleListItem,
} from '../api';
import { InvoiceRecipientDialog, type InvoiceRecipient } from './InvoiceRecipientDialog';
import { PaymentDialog } from './PaymentDialog';
import { ReferrerDialog } from './Referrer';

type Open = { kind: 'deliver' | 'pay' | 'recipient' | 'referrer' | 'cancel'; sale: SaleListItem };

/**
 * The `…` menu of a sale row (#197): the next steps of the sale like on its
 * page (deliver, pay, invoice), "Vermittelt von" and cancelling, without
 * leaving the list. `onInvoice` replaces the invoice step (market day).
 */
export function useSaleRowActions(onInvoice?: (sale: SaleListItem) => void): {
  rowActions: NonNullable<DataTableProps<SaleListItem>['rowActions']>;
  dialogs: ReactNode;
} {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const action = useSaleAction();
  const createInvoice = useCreateInvoice();
  const [open, setOpen] = useState<Open | null>(null);
  const [confirmReturn, setConfirmReturn] = useState(false);
  const write = can('sales.write');
  const close = () => {
    action.reset();
    createInvoice.reset();
    setConfirmReturn(false);
    setOpen(null);
  };
  const run = (a: Parameters<typeof action.mutate>[0], message: string) =>
    action.mutate(a, {
      onSuccess: () => {
        toast.show(message);
        close();
      },
    });
  const createInvoiceFor = (sale: SaleListItem, recipient: InvoiceRecipient) =>
    createInvoice.mutate(
      { sale_id: sale.id, ...recipient },
      {
        onSuccess: (i) => {
          toast.show(t('sales.detail.invoiceCreated'));
          void navigate(`/verkauf/rechnungen/${i.id}`);
        },
        onError: (e) => toast.show(errorMessage(e), 'danger'),
      },
    );
  // Marking as paid is reversible: undo removes the payment again.
  const pay = (sale: SaleListItem, method: PaymentMethod, on: string) =>
    action.mutate(
      { id: sale.id, action: 'pay', method, on },
      {
        onSuccess: () => {
          close();
          toast.show(t('sales.detail.paid'), 'success', {
            label: t('common.undo'),
            onClick: () =>
              action.mutate(
                { id: sale.id, action: 'unpay' },
                { onError: (e) => toast.show(errorMessage(e), 'danger') },
              ),
          });
        },
      },
    );

  const items = (s: SaleListItem): (MenuItem | false)[] => {
    const active = s.status !== 'cancelled';
    const invoice = s.invoice && s.invoice.status !== 'cancelled' ? s.invoice : null;
    const finalInvoice = invoice?.status === 'final';
    const editable = (s.status === 'draft' || s.status === 'ordered') && !finalInvoice;
    const act = (kind: Open['kind']) => () => setOpen({ kind, sale: s });
    return [
      write &&
        (s.status === 'draft' || s.status === 'ordered') && {
          key: 'deliver',
          label: t('sales.detail.deliver'),
          onSelect: act('deliver'),
        },
      write &&
        s.status !== 'draft' &&
        active &&
        !s.payment.paid && {
          key: 'pay',
          label: t('sales.detail.pay'),
          onSelect: act('pay'),
        },
      invoice && can('invoices.read')
        ? {
            key: 'invoice',
            label: t('sales.quick.view', {
              number: invoice.number ?? t('sales.invoice.badge.draft'),
            }),
            onSelect: () =>
              onInvoice ? onInvoice(s) : void navigate(`/verkauf/rechnungen/${invoice.id}`),
          }
        : can('invoices.write') &&
          active &&
          s.status !== 'draft' && {
            key: 'invoice',
            label: t('sales.detail.createInvoice'),
            onSelect: () =>
              onInvoice
                ? onInvoice(s)
                : s.customer
                  ? createInvoiceFor(s, {})
                  : setOpen({ kind: 'recipient', sale: s }),
          },
      write &&
        !editable && {
          key: 'referrer',
          label: t('sales.referrer.change'),
          separated: true,
          onSelect: act('referrer'),
        },
      write && active && !finalInvoice && rowMenu.remove(act('cancel'), t('sales.detail.cancel')),
    ];
  };

  const sale = open?.sale;
  // The list knows nothing about stock: the sale says whether it suffices (SAL-004).
  const delivering = open?.kind === 'deliver' ? open.sale.id : '';
  const deliverDetail = useSale(delivering, delivering !== '');
  const dialogs = (
    <>
      {open?.kind === 'pay' && sale && (
        <PaymentDialog
          open
          onClose={close}
          pending={action.isPending}
          error={action.error}
          onSubmit={(method, on) => pay(sale, method, on)}
        />
      )}
      {open?.kind === 'referrer' && sale && <ReferrerDialog sale={sale} onClose={close} />}
      {open?.kind === 'recipient' && sale && (
        <InvoiceRecipientDialog
          onClose={close}
          totalCents={sale.total_cents}
          pending={createInvoice.isPending}
          error={createInvoice.error}
          onSubmit={(recipient) => createInvoiceFor(sale, recipient)}
        />
      )}
      <ConfirmDialog
        open={open?.kind === 'deliver'}
        onClose={close}
        tone="default"
        title={t('sales.detail.deliverTitle')}
        description={
          (deliverDetail.data?.warnings.length ?? 0) > 0
            ? t('sales.detail.deliverWarning')
            : t('sales.detail.deliverText')
        }
        confirmLabel={t('sales.detail.deliver')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          sale && run({ id: sale.id, action: 'deliver' }, t('sales.detail.delivered'))
        }
      />
      <ConfirmDialog
        open={open?.kind === 'cancel'}
        onClose={close}
        title={t('sales.detail.cancelTitle')}
        description={
          sale?.status === 'delivered' ? (
            <>
              <Text>{t('sales.detail.cancelDelivered')}</Text>
              <Checkbox
                label={t('sales.detail.confirmReturn')}
                checked={confirmReturn}
                onChange={(e) => setConfirmReturn(e.target.checked)}
              />
            </>
          ) : (
            t('sales.detail.cancelText')
          )
        }
        confirmLabel={t('sales.detail.cancel')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          sale &&
          run(
            { id: sale.id, action: 'cancel', confirm_return: confirmReturn },
            t('sales.detail.cancelled'),
          )
        }
      />
    </>
  );

  return {
    rowActions: rowMenu.table<SaleListItem>(
      (s) => s.customer?.name ?? s.market_day?.name ?? t('sales.list.anonymous'),
      items,
    ),
    dialogs,
  };
}
